import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import BackupIcon from '@mui/icons-material/Backup';
import RestoreIcon from '@mui/icons-material/Restore';
import StorageIcon from '@mui/icons-material/Storage';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import { backupService } from '../services/api';
import { formatLocalDateTime } from '../utils/date';

function DatabaseBackup() {
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [revertTarget, setRevertTarget] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchInfo();
    fetchSnapshots();
  }, []);

  const fetchSnapshots = async () => {
    try {
      const data = await backupService.listSnapshots();
      setSnapshots(data);
    } catch {
      setSnapshots([]);
    }
  };

  const fetchInfo = async () => {
    setLoadingInfo(true);
    try {
      const data = await backupService.getInfo();
      setInfo(data);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load database info', severity: 'error' });
    } finally {
      setLoadingInfo(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await backupService.exportDatabase();
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const timestamp = `${pad(now.getDate())}_${pad(now.getMonth()+1)}_${now.getFullYear()}_${pad(now.getHours())}${pad(now.getMinutes())}`;
      const filename = `nscat_backup_${timestamp}.sql`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const verified = response.headers['x-backup-verified'] === 'true';
      const sizeBytes = parseInt(response.headers['x-backup-size-bytes'] || '0', 10);
      const sizeMB = sizeBytes ? ` (${(sizeBytes / 1024 / 1024).toFixed(1)} MB)` : '';
      const verifiedMsg = verified ? ' — integrity verified ✓' : '';
      setSnackbar({ open: true, message: `Backup exported: ${filename}${sizeMB}${verifiedMsg}`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || 'Export failed', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.sql')) {
      setSnackbar({ open: true, message: 'Only .sql backup files are supported', severity: 'error' });
      return;
    }
    setSelectedFile(file);
  };

  const handleImportConfirm = async () => {
    setConfirmOpen(false);
    setImporting(true);
    setUploadProgress(0);
    try {
      await backupService.importDatabase(selectedFile, (e) => {
        if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
      });
      setSnackbar({ open: true, message: 'Database restored successfully. Please refresh the page.', severity: 'success' });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchInfo();
      fetchSnapshots();
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || 'Restore failed', severity: 'error' });
    } finally {
      setImporting(false);
      setUploadProgress(0);
    }
  };

  const handleRevertConfirm = async () => {
    if (!revertTarget) return;
    setReverting(true);
    setRevertTarget(null);
    try {
      await backupService.revertToSnapshot(revertTarget.filename);
      setSnackbar({ open: true, message: `Reverted to snapshot: ${revertTarget.filename}. Please refresh the page.`, severity: 'success' });
      fetchInfo();
      fetchSnapshots();
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || 'Revert failed', severity: 'error' });
    } finally {
      setReverting(false);
    }
  };

  const StatItem = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Chip label={value} size="small" variant="outlined" />
    </Box>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <StorageIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">Database Backup</Typography>
          <Typography variant="body2" color="text.secondary">
            Export or restore the NSCAT PostgreSQL database
          </Typography>
        </Box>
      </Box>

      {/* DB Info Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <InfoOutlinedIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight="bold">Database Info</Typography>
          <Button size="small" onClick={fetchInfo} disabled={loadingInfo} sx={{ ml: 'auto' }}>
            {loadingInfo ? <CircularProgress size={14} /> : 'Refresh'}
          </Button>
        </Box>
        {loadingInfo ? (
          <LinearProgress />
        ) : info ? (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <StatItem label="Database Name" value={info.db_name} />
                <StatItem label="Host" value={info.db_host} />
                <StatItem label="Size" value={info.db_size} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <StatItem label="Users" value={info.table_counts?.users ?? '—'} />
                <StatItem label="Devices" value={info.table_counts?.devices ?? '—'} />
                <StatItem label="Audit Records" value={info.table_counts?.audit_records ?? '—'} />
                <StatItem label="SIEM Entries" value={info.table_counts?.syslog_entries ?? '—'} />
                <StatItem label="PDF Reports" value={info.table_counts?.pdf_reports ?? '—'} />
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Last checked: {info.timestamp ? formatLocalDateTime(info.timestamp) : '—'}
            </Typography>
          </>
        ) : (
          <Alert severity="warning">Could not load database info</Alert>
        )}
      </Paper>

      <Grid container spacing={3}>
        {/* Export */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <BackupIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">Export Backup</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Download a full <code>pg_dump</code> snapshot of the NSCAT database as a
              plain-text <strong>.sql</strong> file. The file includes all tables, data, and
              schema — suitable for disaster recovery or migration.
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 2 }}>
              The export may take a few seconds depending on database size.
            </Alert>
            <Button
              variant="contained"
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={exporting}
              fullWidth
              size="large"
            >
              {exporting ? 'Exporting…' : 'Export Database'}
            </Button>
          </Paper>
        </Grid>

        {/* Import */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <RestoreIcon color="warning" />
              <Typography variant="subtitle1" fontWeight="bold">Import / Restore</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Restore the database from a previously exported <strong>.sql</strong> file.
              This applies the SQL directly to the current database — existing data will be
              overwritten by the backup content.
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ mb: 2 }}>
              <strong>Destructive action.</strong> This will overwrite existing data with the
              backup. Ensure you have a current export before restoring.
            </Alert>

            <input
              type="file"
              accept=".sql"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              fullWidth
              sx={{ mb: 1.5 }}
              disabled={importing}
            >
              {selectedFile ? selectedFile.name : 'Choose .sql File'}
            </Button>

            {selectedFile && (
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon fontSize="small" color="success" />
                <Typography variant="caption" noWrap>
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </Typography>
              </Box>
            )}

            {importing && (
              <Box sx={{ mb: 1.5 }}>
                <LinearProgress variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'} value={uploadProgress} />
                <Typography variant="caption" color="text.secondary">
                  {uploadProgress > 0 ? `Uploading ${uploadProgress}%…` : 'Restoring database…'}
                </Typography>
              </Box>
            )}

            <Tooltip title={!selectedFile ? 'Select a .sql file first' : ''}>
              <span style={{ display: 'block' }}>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
                  onClick={() => setConfirmOpen(true)}
                  disabled={!selectedFile || importing}
                  fullWidth
                  size="large"
                >
                  {importing ? 'Restoring…' : 'Restore Database'}
                </Button>
              </span>
            </Tooltip>
          </Paper>
        </Grid>
      </Grid>

      {/* Saved Versions */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <HistoryIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight="bold">Saved Versions</Typography>
          <Chip label={`${snapshots.length} / 2`} size="small" sx={{ ml: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Auto-snapshots are created before every import
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {snapshots.length === 0 ? (
          <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />}>
            No saved versions yet. A snapshot is automatically created before each restore.
          </Alert>
        ) : (
          snapshots.map((snap, idx) => (
            <Box
              key={snap.filename}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                mb: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <BackupIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Version {snapshots.length - idx} {idx === 0 && <Chip label="Latest" size="small" color="primary" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatLocalDateTime(snap.created_at)} &nbsp;·&nbsp; {(snap.size_bytes / 1024 / 1024).toFixed(1)} MB
                  </Typography>
                </Box>
              </Box>
              <Tooltip title="Revert database to this snapshot">
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={reverting ? <CircularProgress size={14} color="inherit" /> : <RestoreIcon />}
                  onClick={() => setRevertTarget(snap)}
                  disabled={reverting}
                >
                  Revert
                </Button>
              </Tooltip>
            </Box>
          ))
        )}
      </Paper>

      {/* Confirm Restore Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Confirm Database Restore
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to restore the database from <strong>{selectedFile?.name}</strong>.
            <br /><br />
            This action will apply all SQL statements from the backup file to the live
            database. Existing data may be overwritten. This <strong>cannot be undone</strong>.
            <br /><br />
            Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleImportConfirm}>
            Yes, Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Revert Dialog */}
      <Dialog open={Boolean(revertTarget)} onClose={() => setRevertTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Confirm Revert to Snapshot
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to revert the database to the snapshot:<br />
            <strong>{revertTarget?.filename}</strong>
            <br /><br />
            Created: {revertTarget ? formatLocalDateTime(revertTarget.created_at) : ''}
            &nbsp;·&nbsp; Size: {revertTarget ? `${(revertTarget.size_bytes / 1024 / 1024).toFixed(1)} MB` : ''}
            <br /><br />
            All data added or changed after this snapshot was created will be <strong>lost</strong>.
            Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertTarget(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRevertConfirm}>
            Yes, Revert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DatabaseBackup;
